import { PAGE_REGISTRY } from '../utils/navHeaderRegistry';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// UI V3 — shared numbers, the routes that keep their current look, and the page-title lookup.
// Sizes mirror the Encon UI V3 Design System tokens (layout-*).
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export const V3_LAYOUT = {
  topbar: 64,
  nav: 232,
  navStrip: 64,
  rail: 248,
  railStrip: 64,
  gap: 16,
  // At or above this width both edge bars can be part of the page. Below it (laptops) they start as icon strips and open
  // over the content unless the person pins them in a saved view.
  laptopBreakpoint: 1600,
  // Below this width (iPads) the bars hide behind an edge handle and open over the content.
  tabletBreakpoint: 1100,
  // Below this width (an iPad) the right bar cannot be pinned: it is always an icon strip that opens over the page, and it opens twice as wide.
  smallBreakpoint: 1280,
  phoneBreakpoint: 768,
};

// Routes the V3 shell does NOT wrap yet. They render exactly as they do today (old header and all) until they are rebuilt for V3.
//   /transport  — its own full-screen map and panels, rebuilt separately (narrow menu pattern)
//   /login, /oauth — not part of the signed-in dashboard
export const V3_BYPASS_ROUTES = ['/login', '/oauth'];

// Routes that are already built for V3: they are drawn straight on the V3 backdrop, without the plain white page surface the
// pages that have not been rebuilt yet sit on.
export const V3_BARE_ROUTES = ['/', '/hub', '/transport'];

export const isV3BareRoute = (pathname) =>
  V3_BARE_ROUTES.some((route) => (route === '/' ? (pathname === '/' || pathname === '') : (pathname === route || pathname.startsWith(`${route}/`))));

// The pages that have been taken into the new UI. On these the main header and the sub-header are capped at 1400px and centred (as on Home), and the page's content is exactly
// as wide as the header: a page wraps its content in an element with the class "v3-page-content" (Home has its own layout; Settings uses "v3-settings-inner"). When a page is
// taken into the new UI, ADD ITS ROUTE HERE: that is all that is needed for its header, sub-header and content to line up.
export const V3_NEW_UI_ROUTES = ['/', '/hub', '/settings'];

// Pages that are in the new UI whose sub-pages are not (yet): matched on the whole path only, so PI Counts is in the new UI but its count, discrepancy and adjustment pages (/pi-counts/...) keep their current look until they are brought across.
export const V3_NEW_UI_EXACT_ROUTES = ['/pi-counts'];

export const isV3NewUiRoute = (pathname) =>
  V3_NEW_UI_EXACT_ROUTES.includes(pathname)
  || V3_NEW_UI_ROUTES.some((route) => (route === '/' ? (pathname === '/' || pathname === '') : (pathname === route || pathname.startsWith(`${route}/`))));

export const isV3Bypassed = (pathname) =>
  V3_BYPASS_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

// Titles for pages that are not in PAGE_REGISTRY (or whose registry label is not what the page is called).
const V3_EXTRA_TITLES = {
  '/hub': 'Home',
  '/b29-stock-transfer': 'B29 Transfers',
  '/team-management': 'My Team',
  '/cross-deployment-backup': 'Backup',
  '/pi-counts-warehouse': 'PI Counts W/H',
  '/oauth/authorize': 'Authorise',
};

const TITLE_ROUTES = [
  ...PAGE_REGISTRY.map((page) => ({ route: page.route, label: page.label })),
  ...Object.entries(V3_EXTRA_TITLES).map(([route, label]) => ({ route, label })),
];

/** The page title shown in the V3 top bar for a pathname: the longest matching route wins ("/" only matches exactly). */
export const getV3PageTitle = (pathname) => {
  let best = null;
  TITLE_ROUTES.forEach((entry) => {
    const matches = entry.route === '/'
      ? (pathname === '/' || pathname === '')
      : (pathname === entry.route || pathname.startsWith(`${entry.route}/`));
    if (matches && (!best || entry.route.length > best.route.length)) best = entry;
  });
  return best ? best.label : 'Dashboard';
};
